import React, { useState, useEffect, useRef } from 'react';
import { 
  Bell, X, Check, CheckCheck, ExternalLink, AlertTriangle, 
  Clock, FileText, CreditCard, User, Building2, Loader2
} from 'lucide-react';
import { Button } from './ui/button';
import { notificationsApi } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [summary, setSummary] = useState({ unread_count: 0, action_required_count: 0, urgent_count: 0 });
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Fetch notifications on mount and periodically
  useEffect(() => {
    fetchSummary();
    const interval = setInterval(fetchSummary, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSummary = async () => {
    try {
      const data = await notificationsApi.getSummary();
      setSummary(data);
    } catch (error) {
      console.error('Failed to fetch notification summary:', error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const data = await notificationsApi.getNotifications({ limit: 20 });
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      fetchNotifications();
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationsApi.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setSummary(prev => ({ ...prev, unread_count: Math.max(0, prev.unread_count - 1) }));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setSummary(prev => ({ ...prev, unread_count: 0 }));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleDismiss = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await notificationsApi.dismiss(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      toast.error('Failed to dismiss notification');
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await handleMarkAsRead(notification.id);
    }
    if (notification.action_url) {
      // Admin order URLs like /admin/orders/{id} → redirect to /orders/{id} (OrderDetailPage supports admin)
      if (notification.action_url.startsWith('/admin/orders/')) {
        const orderId = notification.action_url.replace('/admin/orders/', '');
        navigate(`/orders/${orderId}`);
      } else {
        navigate(notification.action_url);
      }
      setIsOpen(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order_created':
      case 'new_order_received':
        return <FileText className="w-4 h-4 text-blue-400" />;
      case 'payment_required':
      case 'payment_received':
      case 'payment_verified':
        return <CreditCard className="w-4 h-4 text-emerald-400" />;
      case 'document_rejected':
      case 'payment_reminder':
      case 'document_reminder':
        return <AlertTriangle className="w-4 h-4 text-red-400" />;
      case 'deadline_reminder':
      case 'dsc_expiry_reminder':
        return <Clock className="w-4 h-4 text-amber-400" />;
      case 'new_user_registered':
        return <User className="w-4 h-4 text-purple-400" />;
      case 'company_created':
        return <Building2 className="w-4 h-4 text-cyan-400" />;
      case 'document_uploaded':
      case 'documents_submitted':
      case 'document_approved':
        return <FileText className="w-4 h-4 text-emerald-400" />;
      case 'order_status_changed':
        return <Clock className="w-4 h-4 text-blue-400" />;
      case 'order_completed':
        return <Check className="w-4 h-4 text-emerald-400" />;
      case 'inquiry_received':
        return <Bell className="w-4 h-4 text-amber-400" />;
      default:
        return <Bell className="w-4 h-4 text-slate-400" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'border-l-red-500 bg-red-500/5';
      case 'high': return 'border-l-amber-500 bg-amber-500/5';
      case 'medium': return 'border-l-blue-500';
      default: return 'border-l-slate-500';
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    // Backend stores UTC datetimes without Z suffix - ensure proper UTC parsing
    let date;
    if (typeof dateString === 'string' && !dateString.endsWith('Z') && !dateString.includes('+')) {
      date = new Date(dateString + 'Z');
    } else {
      date = new Date(dateString);
    }
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  const totalBadge = summary.unread_count + summary.action_required_count;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-lg hover:bg-slate-700/50 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className={`w-5 h-5 ${summary.urgent_count > 0 ? 'text-red-400' : 'text-slate-400'}`} />
        {totalBadge > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] text-xs font-bold text-white bg-red-500 rounded-full px-1">
            {totalBadge > 99 ? '99+' : totalBadge}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-slate-900 border border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <div>
              <h3 className="font-semibold text-white">Notifications</h3>
              <p className="text-xs text-slate-400">
                {summary.unread_count} unread, {summary.action_required_count} actions pending
              </p>
            </div>
            {summary.unread_count > 0 && (
              <Button
                onClick={handleMarkAllAsRead}
                variant="ghost"
                size="sm"
                className="text-xs text-slate-400 hover:text-white"
              >
                <CheckCheck className="w-3 h-3 mr-1" />
                Mark all read
              </Button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                <Bell className="w-10 h-10 mb-2 opacity-50" />
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`flex items-start gap-3 p-3 border-l-2 cursor-pointer hover:bg-slate-800/50 transition-colors ${
                    getPriorityColor(notification.priority)
                  } ${!notification.is_read ? 'bg-slate-800/30' : ''}`}
                >
                  <div className="p-2 rounded-lg bg-slate-800">
                    {getNotificationIcon(notification.notification_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm ${notification.is_read ? 'text-slate-300' : 'text-white font-medium'}`}>
                        {notification.title}
                      </p>
                      <button
                        onClick={(e) => handleDismiss(e, notification.id)}
                        className="p-1 rounded hover:bg-slate-700 text-slate-500 hover:text-slate-300"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="text-xs text-slate-400 mt-1 line-clamp-2">{notification.message}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-slate-500">{formatTime(notification.created_at)}</span>
                      {notification.is_action_required && !notification.action_completed && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">
                          Action Required
                        </span>
                      )}
                      {notification.action_url && (
                        <ExternalLink className="w-3 h-3 text-slate-500" />
                      )}
                    </div>
                  </div>
                  {!notification.is_read && (
                    <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-slate-700 flex items-center justify-between">
              <button
                onClick={handleMarkAllAsRead}
                className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
              >
                Mark all as read
              </button>
              <button
                onClick={() => { 
                  fetchNotifications();
                }}
                className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
              >
                Refresh
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
